/**
 * SmileCapture — Continuity System Phase 3 (Successor identity verification).
 *
 * Wraps Smile ID's <smart-camera-web> web component in a Shadcn Dialog and
 * forwards the captured selfie + government ID images (both base64) back to
 * the parent through onCapture.
 *
 * UX notes:
 *   - The component handles its own liveness check, image quality validation
 *     and review screens. We only listen for the final publish event.
 *   - We also expose `document-capture-modes="camera,upload"` so successors
 *     on shaky cameras can fall back to uploading a clear photo of their ID.
 */
import React, { useEffect, useRef, useState } from 'react';
import '@smileid/web-components/smart-camera-web';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { ShieldCheck } from 'lucide-react';

const pickImage = (images, typeId) =>
  images.find((i) => Number(i.image_type_id) === typeId)?.image;

export const SmileCapture = ({ open, onClose, onCapture }) => {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) { setError(null); return undefined; }
    const root = containerRef.current;
    if (!root) return undefined;
    const handler = (e) => {
      const images = e.detail?.images || [];
      const selfieB64 = pickImage(images, 2);
      const idB64 = pickImage(images, 3) || pickImage(images, 7);
      if (!selfieB64 || !idB64) {
        setError('We could not capture both your selfie and your ID. Please try again.');
        return;
      }
      onCapture({ selfieB64, idB64 });
    };
    root.addEventListener('smart-camera-web.publish', handler);
    return () => root.removeEventListener('smart-camera-web.publish', handler);
  }, [open, onCapture]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-3xl p-0 overflow-hidden"
        data-testid="smile-capture-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-2 border-b border-slate-100">
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <ShieldCheck className="w-5 h-5 text-emerald-700" />
            Verify your identity
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            Take a quick selfie and a photo of your government-issued ID.
            Please use good lighting. The flow runs locally in your browser.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p
            className="px-6 py-2 text-sm text-rose-700 bg-rose-50 border-b border-rose-100"
            data-testid="smile-capture-error"
          >
            {error}
          </p>
        )}
        <div ref={containerRef} className="p-4 bg-white">
          <smart-camera-web
            capture-id=""
            document-capture-modes="camera,upload"
            data-testid="smile-camera"
          ></smart-camera-web>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SmileCapture;
